import type { EditorialSource } from './editorial';

export interface Author {
  id: string;
  name: string;
  bio: string;
}

export interface BlogPost {
  id: string;
  title: string;
  seoTitle?: string;
  slug: string;
  excerpt: string;
  // content is now removed and loaded dynamically from .md files
  author: string;
  authorId: string;
  category: 'Guide' | 'News' | 'Tips' | 'Regulations';
  publishedDate: string;
  lastUpdated: string;
  readTime: number;
  imageUrl: string;
  tags: string[];
  featured: boolean;
  sources: EditorialSource[];
  reviewedAt?: string;
  adsEligible: boolean;
}

export const authors: Author[] = [{
  id: 'airport-team',
  name: 'airports.ng Editorial Team',
  bio: 'The airports.ng Editorial Team prepares practical travel guides using published information from airport operators, airlines and public authorities. Sources and review dates are shown with each reviewed guide.'
}];

const rawBlogPosts: Omit<BlogPost, 'sources' | 'reviewedAt' | 'adsEligible'>[] = [
  {
    id: 'bringing-phone-to-nigeria-ncc-verification',
    title: 'Bringing a Phone to Nigeria: NCC Verification and the 30-Day Rule (2026)',
    seoTitle: 'Bringing a Phone to Nigeria: NCC Rules (2026)',
    slug: 'bringing-phone-to-nigeria-ncc-verification',
    excerpt: 'Bringing a phone to Nigeria? Check NCC verification, the 30-day visitor allowance, roaming, gift phones and what to do if your device is unverified.',
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Regulations',
    publishedDate: '2026-10-23',
    lastUpdated: '2026-10-23',
    readTime: 7,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['NCC', 'Phone Verification', 'Diaspora Travel', 'Christmas Travel', 'SIM Cards'],
    featured: true
  },
  {
    id: 'nigeria-landing-exit-card-guide',
    title: 'Nigeria Landing and Exit Cards 2026: Who Needs Them, How to Apply and Common Errors',
    seoTitle: 'Nigeria Landing & Exit Cards: Apply and Fix Errors (2026)',
    slug: 'nigeria-landing-exit-card-guide',
    excerpt: 'Nigeria landing and exit cards explained: who needs them, Nigerian-passport exemptions, when to apply, official forms and fixes for common portal errors.',
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Regulations',
    publishedDate: '2026-10-23',
    lastUpdated: '2026-10-23',
    readTime: 7,
    imageUrl: 'https://images.unsplash.com/photo-1569629743817-70d8db6c323b?w=1200&q=80',
    tags: ['Landing Card', 'Exit Card', 'Nigeria Immigration', 'Diaspora Travel', 'Christmas Travel'],
    featured: true
  },
  {
    id: 'lagos-airport-los-terminal-transfer-guide-2026',
    title: "Connecting International and Domestic Flights at Lagos Airport",
    seoTitle: "Connecting International and Domestic Flights at Lagos Airport",
    slug: 'lagos-airport-los-terminal-transfer-guide-2026',
    excerpt: "Plan a Lagos connection around terminal changes, entry permission, baggage, deadlines and protection if a flight is delayed.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-07-27',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Lagos Airport', 'Terminal Transfer', 'LOS', 'MMIA', 'MMA2', 'GAT', 'Connecting Flights'],
    featured: true
  },
  {
    id: 'travelling-to-nigeria-with-expired-passport-2026',
    title: "Expired Nigerian Passport: Returning Home and Planning Your Departure",
    seoTitle: "Expired Nigerian Passport: Returning Home and Planning Your Departure",
    slug: 'travelling-to-nigeria-with-expired-passport-2026',
    excerpt: "How to seek official confirmation, arrange passport renewal or emergency documentation, and plan both legs of a journey with an expired Nigerian passport.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Regulations',
    publishedDate: '2026-07-23',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1569629743817-70d8db6c323b?w=1200&q=80',
    tags: ['Expired Passport', 'Dual Citizenship', 'Nigeria Immigration', 'Emergency Travel Certificate', 'Diaspora Travel'],
    featured: true
  },
  {
    id: 'nigeria-e-yellow-card-online-guide-2026',
    title: "Nigeria e-Yellow Card: Registration, Vaccination and Travel Checks",
    seoTitle: "Nigeria e-Yellow Card: Registration, Vaccination and Travel Checks",
    slug: 'nigeria-e-yellow-card-online-guide-2026',
    excerpt: "Find the official Nigerian Yellow Card registration route, understand vaccination-certificate timing and check individual requirements before travel.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-06-10',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: '/images/nigeria-yellow-card.png',
    tags: ['Yellow Card', 'Vaccination', 'Travel Guide', 'Requirements'],
    featured: false
  },
  {
    id: 'air-peace-lagos-london-gatwick-guide-2026',
    title: "Air Peace to London: Airport, Baggage and Student-Fare Checks",
    seoTitle: "Air Peace to London: Airport, Baggage and Student-Fare Checks",
    slug: 'air-peace-lagos-london-gatwick-guide-2026',
    excerpt: "Check the London airport, ticket-specific baggage, student-offer terms and connecting-flight arrangements before booking Air Peace.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-06-05',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: '/images/air-peace-london.png',
    tags: ['Air Peace', 'London Gatwick', 'International Flights', 'Student Discount', 'Baggage Allowance'],
    featured: false
  },
  {
    id: 'nigerian-airlines-baggage-allowance-fees-2026',
    title: "Nigerian Airline Baggage: Allowances, Fees and What to Check",
    seoTitle: "Nigerian Airline Baggage: Allowances, Fees and What to Check",
    slug: 'nigerian-airlines-baggage-allowance-fees-2026',
    excerpt: "Read your ticket allowance, distinguish pieces from total weight, and confirm excess charges with the operating airline.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-06-13',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1565026057447-bc90a3dceb87?w=1200&q=80',
    tags: ['Baggage Allowance', 'Excess Fees', 'Air Peace', 'Ibom Air', 'Green Africa'],
    featured: true
  },
  {
    id: 'flight-delay-compensation-nigeria-2026',
    title: "Flight Delays in Nigeria: Document Your Claim and Ask for Help",
    seoTitle: "Flight Delays in Nigeria: Document Your Claim and Ask for Help",
    slug: 'flight-delay-compensation-nigeria-2026',
    excerpt: "Use NCAA consumer-protection rules to distinguish assistance, reimbursement and compensation, and prepare a clear airline complaint.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-02-28',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?w=1200&q=80',
    tags: ['NCAA', 'Passenger Rights', 'Compensation', 'Delays'],
    featured: false
  },
  {
    id: 'visa-on-arrival-nigeria-2026',
    title: "Nigeria Visa on Arrival Has Ended: What to Do Instead",
    seoTitle: "Nigeria Visa on Arrival Has Ended: What to Do Instead",
    slug: 'visa-on-arrival-nigeria-2026',
    excerpt: "Nigeria has replaced Visa on Arrival with advance visa arrangements. Find the official eVisa route and check the documents for your journey.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Regulations',
    publishedDate: '2026-03-05',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1569629743817-70d8db6c323b?w=1200&q=80',
    tags: ['VoA', 'Visa', 'Business Travel', 'Immigration'],
    featured: true
  },
  {
    id: 'lagos-to-abuja-flight-guide-2026',
    title: "Lagos to Abuja: Compare the Whole Journey",
    seoTitle: "Lagos to Abuja: Compare the Whole Journey",
    slug: 'lagos-to-abuja-flight-guide-2026',
    excerpt: "Plan a Lagos–Abuja flight using current fares, exact terminals, airport deadlines and transport at both ends.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-03-12',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=1200&q=80',
    tags: ['Lagos', 'Abuja', 'Domestic Flights', 'Transport'],
    featured: false
  },
  {
    id: 'currency-exchange-nigerian-airports-2026',
    title: "Cash and Mobile Data After Landing in Nigeria",
    seoTitle: "Cash and Mobile Data After Landing in Nigeria",
    slug: 'currency-exchange-nigerian-airports-2026',
    excerpt: "Prepare airport transport payment and a working connection, and handle SIM or money-service delays without depending on instant activation.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-03-19',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
    tags: ['Currency', 'SIM Card', 'Arrivals', 'Tips'],
    featured: false
  },
  {
    id: 'upgraded-airport-terminals-kano-enugu-port-harcourt',
    title: "Kano, Enugu and Port Harcourt Airports: Plan the Right Arrival",
    seoTitle: "Kano, Enugu and Port Harcourt Airports: Plan the Right Arrival",
    slug: 'upgraded-airport-terminals-kano-enugu-port-harcourt',
    excerpt: "Compare regional-airport journeys by actual flight availability, terminal instructions, onward transport and assistance needs.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-03-26',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80',
    tags: ['Terminals', 'Regional', 'Upgrades', 'PHC', 'KAN'],
    featured: false
  },
  {
    id: 'prohibited-items-domestic-flights-nigeria',
    title: "Restricted Items on Nigerian Domestic Flights: A Packing Check",
    seoTitle: "Restricted Items on Nigerian Domestic Flights: A Packing Check",
    slug: 'prohibited-items-domestic-flights-nigeria',
    excerpt: "Separate cabin restrictions from dangerous goods, check batteries and liquids, and ask the airline about unusual items before packing.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Regulations',
    publishedDate: '2026-03-31',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?w=1200&q=80',
    tags: ['Security', 'FAAN', 'Prohibited Items', 'Luggage'],
    featured: false
  },
  {
    id: 'how-to-book-cheap-flights-nigeria-secrets',
    title: "How to Compare Nigerian Flight Prices Without Booking Myths",
    seoTitle: "How to Compare Nigerian Flight Prices Without Booking Myths",
    slug: 'how-to-book-cheap-flights-nigeria-secrets',
    excerpt: "Find the best usable fare by comparing dates, baggage, fees and change rules rather than relying on a supposedly guaranteed cheap booking window.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-04-03',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Budget Flights', 'Tips', 'Booking', 'Savings'],
    featured: false
  },
  {
    id: 'understanding-airport-taxes-hidden-fees-nigeria',
    title: "Understanding a Nigerian Flight Price: Fare, Charges and Extras",
    seoTitle: "Understanding a Nigerian Flight Price: Fare, Charges and Extras",
    slug: 'understanding-airport-taxes-hidden-fees-nigeria',
    excerpt: "Read the full ticket price, question unclear line items and compare optional services before payment.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=1200&q=80',
    tags: ['Taxes', 'PSC', 'FAAN', 'Hidden Fees'],
    featured: true
  },
  {
    id: 'los-airport-guide-2026',
    title: "Lagos Airport Terminals: MMIA, MMA2 and Domestic Terminal 1",
    seoTitle: "Lagos Airport Terminals: MMIA, MMA2 and Domestic Terminal 1",
    slug: 'los-airport-guide-2026',
    excerpt: "Understand Lagos airport terminal names, find the right building for your flight and locate official passenger help.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-02-01',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Lagos Airport', 'LOS', 'Travel Guide', 'Immigration'],
    featured: true
  },
  {
    id: 'safest-domestic-airlines-2026',
    title: "How to Assess Nigerian Airline Safety and Reliability",
    seoTitle: "How to Assess Nigerian Airline Safety and Reliability",
    slug: 'safest-domestic-airlines-2026',
    excerpt: "Check regulatory information and compare practical recovery options without relying on unsupported airline safety rankings.",
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-02-15',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=1200&q=80',
    tags: ['Domestic Flights', 'Airlines', 'Safety', 'Rankings'],
    featured: true
  },
  // ============================================
  // FEATURED POSTS
  // ============================================
  {
    id: 'lagos-airport-guide-2026',
    title: "Arriving Internationally at Lagos Airport: A Practical Checklist",
    seoTitle: "Arriving Internationally at Lagos Airport: A Practical Checklist",
    slug: 'comprehensive-lagos-airport-guide-2026',
    excerpt: "Prepare your documents, baggage collection, onward connection and airport pickup for an international arrival in Lagos.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Lagos Airport', 'LOS', 'Travel Guide', 'Immigration', 'Terminal Map'],
    featured: true
  },
  {
    id: 'nigeria-visa-requirements',
    title: "Nigeria Visa Requirements: Choose the Right Application",
    seoTitle: "Nigeria Visa Requirements: Choose the Right Application",
    slug: 'nigeria-visa-requirements-explained',
    excerpt: "A practical guide to checking visa purpose, eligibility, supporting documents and entry conditions using Nigeria Immigration Service sources.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Regulations',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1569629743817-70d8db6c323b?w=1200&q=80',
    tags: ['Visa', 'Immigration', 'VoA', 'Travel Document', 'Nigeria Entry'],
    featured: true
  },
  {
    id: 'domestic-flights-nigeria',
    title: "Planning a Domestic Flight in Nigeria: Routes and Booking Checks",
    seoTitle: "Planning a Domestic Flight in Nigeria: Routes and Booking Checks",
    slug: 'domestic-flights-nigeria-airlines-routes',
    excerpt: "Compare live flight options, terminal assignments, baggage and change conditions for a journey between Nigerian cities.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=1200&q=80',
    tags: ['Domestic Flights', 'Air Peace', 'Ibom Air', 'Airlines', 'Travel Guide'],
    featured: true
  },

  // ============================================
  // MIGRATED BLOG POSTS
  // ============================================
  {
    id: 'tips-first-time-flyers',
    title: "Your First Flight in Nigeria: From Booking to Baggage Collection",
    seoTitle: "Your First Flight in Nigeria: From Booking to Baggage Collection",
    slug: 'tips-first-time-flyers-nigeria',
    excerpt: "A simple walkthrough of check-in, bag drop, security, boarding and arrival for a first-time flyer.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2024-08-20',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['First Time Flyer', 'Beginner Guide', 'Airport Tips', 'Check-in'],
    featured: false
  },
  {
    id: 'top-5-holiday-destinations',
    title: "Five Holiday Destinations from Nigeria: Entry Checks Before Booking",
    seoTitle: "Five Holiday Destinations from Nigeria: Entry Checks Before Booking",
    slug: 'top-5-holiday-destinations-from-nigeria',
    excerpt: "Compare Ghana, Rwanda, Kenya, Maldives and Seychelles using their official entry services and the full cost of the itinerary.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=1200&q=80',
    tags: ['Holiday Destinations', 'Visa Free', 'Vacation', 'Travel Ideas'],
    featured: false
  },
  {
    id: 'traveling-with-children',
    title: "Flying with Children in Nigeria: Documents, Bags and Airport Plans",
    seoTitle: "Flying with Children in Nigeria: Documents, Bags and Airport Plans",
    slug: 'traveling-with-children-nigeria-airports',
    excerpt: "Prepare each child’s documents, confirm infant-equipment rules and arrange a manageable airport journey for the family.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-01-28',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1200&q=80',
    tags: ['Family Travel', 'Children', 'Parenting', 'Airport Tips'],
    featured: false
  },
  {
    id: 'airport-security-tips',
    title: "Airport Security in Nigeria: Prepare Without Guessing the Rules",
    seoTitle: "Airport Security in Nigeria: Prepare Without Guessing the Rules",
    slug: 'airport-security-nigeria-tips',
    excerpt: "Organise your bag, follow screening instructions and resolve uncertainty about restricted items through official channels.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2024-11-15',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?w=1200&q=80',
    tags: ['Security', 'FAAN', 'Prohibited Items', 'Safety'],
    featured: false
  },
  {
    id: 'abuja-airport-guide',
    title: "Abuja Airport: Terminals, Passenger Help and City Transfers",
    seoTitle: "Abuja Airport: Terminals, Passenger Help and City Transfers",
    slug: 'abuja-airport-abv-terminals-services',
    excerpt: "Find official help at Abuja airport, confirm your terminal and arrange transport or assistance before travelling.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2024-11-10',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=1200&q=80',
    tags: ['Abuja Airport', 'ABV', 'Airport Guide', 'Transport'],
    featured: false
  },
  {
    id: 'business-travel-lounges',
    title: "Nigerian Airport Lounges: Check Access Before You Pay",
    seoTitle: "Nigerian Airport Lounges: Check Access Before You Pay",
    slug: 'best-airport-lounges-nigeria-business-travel',
    excerpt: "Compare lounge access by terminal, entry conditions, guest allowance and services you actually need.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-03-05',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1540575861501-7cf05a4b125a?w=1200&q=80',
    tags: ['Business Travel', 'Airport Lounges', 'VIP', 'Priority Pass'],
    featured: false
  },
  {
    id: 'currency-exchange-tips',
    title: "Changing Money for Nigeria: Compare the Amount You Receive",
    seoTitle: "Changing Money for Nigeria: Compare the Amount You Receive",
    slug: 'currency-exchange-nigeria-guide-2026',
    excerpt: "Compare exchange quotes, card charges and transfer costs using the net amount received, with checks for authorised providers.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-02-14',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
    tags: ['Currency Exchange', 'Money', 'Travel Finance', 'Forex'],
    featured: false
  },
  {
    id: 'navigating-lagos-airport',
    title: "Departing Lagos Airport: Documents, Check-in and Boarding",
    seoTitle: "Departing Lagos Airport: Documents, Check-in and Boarding",
    slug: 'navigating-lagos-airport-los-guide',
    excerpt: "Work backwards from your airline deadlines, confirm the terminal and organise documents and baggage for departure from Lagos.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2024-11-25',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Lagos Airport', 'Navigation', 'Terminal Guide', 'MMA2'],
    featured: false
  },
  {
    id: 'packing-essentials',
    title: "Packing for Nigeria: Build a List Around Your Itinerary",
    seoTitle: "Packing for Nigeria: Build a List Around Your Itinerary",
    slug: 'packing-essentials-nigerian-trip',
    excerpt: "Prepare documents, clothing, medication, electronics and payment backups for your actual Nigerian journey.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2024-10-10',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=1200&q=80',
    tags: ['Packing', 'Travel Essentials', 'Trip Preparation'],
    featured: false
  },
  {
    id: 'lost-luggage-guide',
    title: "Missing or Damaged Baggage in Nigeria: What to Record",
    seoTitle: "Missing or Damaged Baggage in Nigeria: What to Record",
    slug: 'lost-luggage-what-to-do-nigerian-airports',
    excerpt: "Report a missing or damaged bag, keep a useful evidence trail and distinguish tracing from a written compensation claim.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-01-10',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1523289333742-be1143f6b766?w=1200&q=80',
    tags: ['Lost Luggage', 'Baggage', 'Passenger Rights', 'NCAA'],
    featured: false
  },
  {
    id: 'managing-layovers',
    title: "Planning a Layover: Airside Access, Hotels and Missed Connections",
    seoTitle: "Planning a Layover: Airside Access, Hotels and Missed Connections",
    slug: 'managing-layovers-international-airports',
    excerpt: "Decide whether to stay in the terminal or arrange an overnight stop by checking entry permission, baggage and onward check-in.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1529074963764-98f45c47344b?w=1200&q=80',
    tags: ['Layovers', 'Transit', 'Connections', 'Hotels'],
    featured: false
  },
  {
    id: 'airport-codes-explained',
    title: "Nigerian Airport Codes: Check the Destination Before You Book",
    seoTitle: "Nigerian Airport Codes: Check the Destination Before You Book",
    slug: 'understanding-nigerian-airport-codes',
    excerpt: "Understand three-letter airport codes, avoid similar-code mistakes and distinguish a terminal name from an airport.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2024-10-15',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Airport Codes', 'IATA', 'Aviation', 'Education'],
    featured: false
  },
  {
    id: 'car-rental-guide',
    title: "Airport Cars and Drivers in Nigeria: What to Confirm Before Booking",
    seoTitle: "Airport Cars and Drivers in Nigeria: What to Confirm Before Booking",
    slug: 'renting-car-nigerian-airports-guide',
    excerpt: "Compare airport transfers and car hire by driver arrangements, included costs, vehicle suitability and pickup instructions.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-03-22',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&q=80',
    tags: ['Car Rental', 'Transport', 'Driving', 'Logistics'],
    featured: false
  },
  {
    id: 'sustainable-aviation',
    title: "Comparing the Climate Impact of a Flight",
    seoTitle: "Comparing the Climate Impact of a Flight",
    slug: 'sustainable-aviation-nigeria-eco-friendly',
    excerpt: "Use a documented emissions estimate, compare complete journeys and understand the limits of carbon-offset claims.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-05-15',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
    tags: ['Sustainability', 'Eco-Travel', 'Ibom Air', 'Green Africa'],
    featured: false
  },
  {
    id: 'digital-nomad-guide',
    title: "Planning Remote Work During a Visit to Nigeria",
    seoTitle: "Planning Remote Work During a Visit to Nigeria",
    slug: 'digital-nomad-travel-nigeria-guide',
    excerpt: "Check permission for your activities, test the workspace and connectivity arrangements, and prepare a practical backup plan.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-05-01',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=80',
    tags: ['Digital Nomad', 'Remote Work', 'Internet', 'Starlink'],
    featured: false
  },
  {
    id: 'domestic-vs-international',
    title: "Domestic and International Flights: What Changes in Your Preparation",
    seoTitle: "Domestic and International Flights: What Changes in Your Preparation",
    slug: 'domestic-vs-international-flights-nigeria',
    excerpt: "Compare documents, terminal checks, baggage and connections when moving from domestic to international travel.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2024-11-30',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=1200&q=80',
    tags: ['Domestic Flights', 'International Flights', 'Comparison', 'Travel Tips'],
    featured: false
  },
  {
    id: 'carry-on-essentials',
    title: "Carry-On Essentials for a Nigerian Flight",
    seoTitle: "Carry-On Essentials for a Nigerian Flight",
    slug: 'essential-items-carry-on-nigeria-flights',
    excerpt: "Pack the small bag you will need during the flight, a delay or an unexpected separation from checked luggage.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-04-07',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=1200&q=80',
    tags: ['Carry-On', 'Packing', 'Travel Essentials'],
    featured: false
  },
  {
    id: 'travel-insurance-guide',
    title: "Travel Insurance for Nigeria: Read the Cover Before Buying",
    seoTitle: "Travel Insurance for Nigeria: Read the Cover Before Buying",
    slug: 'travel-insurance-guide-nigeria',
    excerpt: "Check eligibility, medical and evacuation cover, exclusions, disruption benefits and the claims process for your actual journey.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-04-10',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80',
    tags: ['Travel Insurance', 'Protection', 'Safety', 'Visa Requirements'],
    featured: false
  },
  {
    id: 'accessible-travel',
    title: "Accessible Air Travel in Nigeria: Arrange the Assistance You Need",
    seoTitle: "Accessible Air Travel in Nigeria: Arrange the Assistance You Need",
    slug: 'accessible-travel-nigeria-disability-guide',
    excerpt: "Prepare specific assistance requests, mobility-aid details and transfer arrangements with the airline before travelling through Nigerian airports.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Guide',
    publishedDate: '2026-03-22',
    lastUpdated: '2026-10-23',
    readTime: 3,
    imageUrl: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80',
    tags: ['Accessibility', 'Disability', 'Special Needs', 'Wheelchair'],
    featured: false
  },
  {
    id: 'safety-in-nigeria',
    title: "Travel Safety in Nigeria: Check the Route and Prepare Your Arrival",
    seoTitle: "Travel Safety in Nigeria: Check the Route and Prepare Your Arrival",
    slug: 'safety-in-nigeria-travel-guide',
    excerpt: "Use current official advisories, trusted local arrangements and an emergency plan instead of unsupported city safety scores.",
    
    author: 'airports.ng Editorial Team',
    authorId: 'airport-team',
    category: 'Tips',
    publishedDate: '2026-04-28',
    lastUpdated: '2026-10-23',
    readTime: 2,
    imageUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&q=80',
    tags: ['Safety', 'Security', 'Travel Tips', 'Nigeria Travel'],
    featured: false
  },
];

// Reviewed references are maintained per article; new entries remain ineligible for ads.
const articleSources: Record<string, EditorialSource[]> = {
  "bringing-phone-to-nigeria-ncc-verification": [
    {
      "title": "Device Checker FAQ",
      "url": "https://www.ncc.gov.ng/frequently-asked-questions/ncc-device-checker-and-type-approval?page=2"
    },
    {
      "title": "NCC verification guide",
      "url": "https://www.ncc.gov.ng/DeviceCheckerGuide"
    },
    {
      "title": "device-identification guide",
      "url": "https://support.apple.com/en-gb/108037"
    },
    {
      "title": "NCC Device Checker page",
      "url": "https://www.ncc.gov.ng/devicechecker"
    },
    {
      "title": "Apple's unlocking instructions",
      "url": "https://support.apple.com/en-gb/109316"
    },
    {
      "title": "Google's carrier guidance",
      "url": "https://support.google.com/pixelphone/answer/7107188?hl=en"
    },
    {
      "title": "guidance for foreign visitors",
      "url": "https://www.ncc.gov.ng/frequently-asked-questions/foreigners-living-nigeria-or-visiting-abroad"
    },
    {
      "title": "NIN and SIM guidance",
      "url": "https://ncc.gov.ng/media-center/public-notices/frequently-asked-questions-nin-and-sim-integration"
    },
    {
      "title": "SIM troubleshooting guide",
      "url": "https://support.apple.com/en-gb/108914"
    },
    {
      "title": "connectivity guide",
      "url": "https://support.google.com/pixelphone/answer/14116080?hl=en"
    },
    {
      "title": "Apple's international eSIM guidance",
      "url": "https://support.apple.com/en-gb/118227"
    }
  ],
  "nigeria-landing-exit-card-guide": [
    {
      "title": "NIS FAQ",
      "url": "https://lecard.immigration.gov.ng/faq"
    },
    {
      "title": "lecard.immigration.gov.ng",
      "url": "https://lecard.immigration.gov.ng/"
    },
    {
      "title": "Source: NIS description of the service",
      "url": "https://lecard.immigration.gov.ng/about"
    },
    {
      "title": "Source: GOV.UK Nigeria entry requirements",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/entry-requirements"
    },
    {
      "title": "Get Your Last Card",
      "url": "https://lecard.immigration.gov.ng/get-last-card"
    },
    {
      "title": "official contact page",
      "url": "https://lecard.immigration.gov.ng/contact-us"
    }
  ],
  "lagos-airport-los-terminal-transfer-guide-2026": [
    {
      "title": "FAAN",
      "url": "https://faan.gov.ng/passenger-experience/"
    },
    {
      "title": "MMA2",
      "url": "https://mma2.ng/flight-and-airline/"
    }
  ],
  "travelling-to-nigeria-with-expired-passport-2026": [
    {
      "title": "NIS passport service",
      "url": "https://immigration.gov.ng/passports/"
    },
    {
      "title": "announced a Single Travel Emergency Passport",
      "url": "https://immigration.gov.ng/nis-partners-with-stakeholders-to-combat-smuggling-of-migrants-announces-biometric-emergency-passport/"
    }
  ],
  "nigeria-e-yellow-card-online-guide-2026": [
    {
      "title": "Nigerian Ministry of Health's Port Health FAQ",
      "url": "https://health.gov.ng/faqs/"
    },
    {
      "title": "NHS yellow-fever vaccination guidance",
      "url": "https://www.nhs.uk/conditions/yellow-fever/vaccination/"
    },
    {
      "title": "yellowcard.health.gov.ng",
      "url": "https://yellowcard.health.gov.ng/"
    }
  ],
  "air-peace-lagos-london-gatwick-guide-2026": [
    {
      "title": "Air Peace's official booking site",
      "url": "https://flyairpeace.com/"
    },
    {
      "title": "airport's rail information",
      "url": "https://www.gatwickairport.com/transport-options/train.html"
    },
    {
      "title": "Air Peace's baggage policy",
      "url": "https://flyairpeace.com/baggage-policy/"
    }
  ],
  "nigerian-airlines-baggage-allowance-fees-2026": [
    {
      "title": "Ibom Air's baggage page",
      "url": "https://www.ibomair.com/travel-information/baggage/"
    }
  ],
  "flight-delay-compensation-nigeria-2026": [
    {
      "title": "NCAA Part 19: Consumer Protection",
      "url": "https://ncaa.gov.ng/media/co1nbi24/nig-cars-part-19-consumer-protection.pdf"
    },
    {
      "title": "NCAA complaint instructions",
      "url": "https://ncaa.gov.ng/report-forms/complaint-form/"
    }
  ],
  "visa-on-arrival-nigeria-2026": [
    {
      "title": "UK government's Nigeria entry guidance",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/entry-requirements"
    },
    {
      "title": "Nigeria Immigration Service's visa directory",
      "url": "https://immigration.gov.ng/nigerian-visa/"
    },
    {
      "title": "official eVisa service",
      "url": "https://evisa.immigration.gov.ng/"
    }
  ],
  "lagos-to-abuja-flight-guide-2026": [
    {
      "title": "IATA code lookup",
      "url": "https://www.iata.org/codes/"
    },
    {
      "title": "FAAN's Passenger Experience Hub",
      "url": "https://faan.gov.ng/passenger-experience/"
    }
  ],
  "currency-exchange-nigerian-airports-2026": [
    {
      "title": "NCC's visitor guidance",
      "url": "https://www.ncc.gov.ng/frequently-asked-questions/foreigners-living-nigeria-or-visiting-abroad"
    },
    {
      "title": "CBN information",
      "url": "https://www.cbn.gov.ng/AboutCBN/Reforms.html"
    }
  ],
  "upgraded-airport-terminals-kano-enugu-port-harcourt": [
    {
      "title": "FAAN airport information",
      "url": "https://faan.gov.ng/local-airports/"
    },
    {
      "title": "FAAN passenger support",
      "url": "https://faan.gov.ng/passenger-experience/"
    },
    {
      "title": "UK government's Nigeria safety guidance",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/safety-and-security"
    }
  ],
  "prohibited-items-domestic-flights-nigeria": [
    {
      "title": "IATA passenger dangerous-goods guide",
      "url": "https://www.iata.org/en/programs/cargo/dangerous-goods/dgr-guidance-passengers/"
    },
    {
      "title": "MMA2's passenger guidance",
      "url": "https://mma2.ng/flight-and-airline/"
    }
  ],
  "how-to-book-cheap-flights-nigeria-secrets": [
    {
      "title": "Ibom Air baggage page",
      "url": "https://www.ibomair.com/travel-information/baggage/"
    },
    {
      "title": "airline's published booking service",
      "url": "https://flyairpeace.com/"
    }
  ],
  "understanding-airport-taxes-hidden-fees-nigeria": [
    {
      "title": "NCAA consumer-protection regulations",
      "url": "https://ncaa.gov.ng/media/co1nbi24/nig-cars-part-19-consumer-protection.pdf"
    },
    {
      "title": "Air Peace's baggage policy",
      "url": "https://flyairpeace.com/baggage-policy/"
    }
  ],
  "los-airport-guide-2026": [
    {
      "title": "FAAN's Passenger Experience Hub",
      "url": "https://faan.gov.ng/passenger-experience/"
    },
    {
      "title": "MMA2 terminal",
      "url": "https://mma2.ng/flight-and-airline/"
    },
    {
      "title": "help and contacts page",
      "url": "https://mma2.ng/help-contacts/"
    },
    {
      "title": "car-park information and rates",
      "url": "https://mma2.ng/car-park/"
    }
  ],
  "safest-domestic-airlines-2026": [
    {
      "title": "NCAA's air-operator certification guidance",
      "url": "https://ncaa.gov.ng/media/uwlneyan/ac-ops001_certification-of-air-operator_amended_january-2024.pdf"
    },
    {
      "title": "NCAA website",
      "url": "https://ncaa.gov.ng/"
    }
  ],
  "comprehensive-lagos-airport-guide-2026": [
    {
      "title": "Nigeria Immigration Service",
      "url": "https://immigration.gov.ng/nigerian-visa/"
    },
    {
      "title": "FAAN's Passenger Experience Hub",
      "url": "https://faan.gov.ng/passenger-experience/"
    }
  ],
  "nigeria-visa-requirements-explained": [
    {
      "title": "NIS visa directory",
      "url": "https://immigration.gov.ng/nigerian-visa/"
    }
  ],
  "domestic-flights-nigeria-airlines-routes": [
    {
      "title": "Air Peace",
      "url": "https://flyairpeace.com/"
    },
    {
      "title": "Ibom Air",
      "url": "https://www.ibomair.com/"
    },
    {
      "title": "Ibom Air's FAQs",
      "url": "https://www.ibomair.com/faqs/"
    }
  ],
  "tips-first-time-flyers-nigeria": [
    {
      "title": "MMA2's passenger guidance",
      "url": "https://mma2.ng/flight-and-airline/"
    },
    {
      "title": "FAAN's Passenger Experience Hub",
      "url": "https://faan.gov.ng/passenger-experience/"
    }
  ],
  "top-5-holiday-destinations-from-nigeria": [
    {
      "title": "Ghana Immigration's applicant guide",
      "url": "https://evisa.immigration.gov.gh/applicant-guide.html"
    },
    {
      "title": "Rwanda's immigration guidance",
      "url": "https://www.migration.gov.rw/our-services/visa-issued-under-special-arrangement"
    },
    {
      "title": "official Kenya eTA service",
      "url": "https://etakenya.go.ke/"
    },
    {
      "title": "Maldives Immigration",
      "url": "https://www2.immigration.gov.mv/tourist-visa/"
    },
    {
      "title": "Seychelles immigration service",
      "url": "https://ics.gov.sc/visa-and-travel/visiting-seychelles"
    }
  ],
  "traveling-with-children-nigeria-airports": [
    {
      "title": "Ibom Air's FAQs",
      "url": "https://www.ibomair.com/faqs/"
    }
  ],
  "airport-security-nigeria-tips": [
    {
      "title": "IATA passenger dangerous-goods guidance",
      "url": "https://www.iata.org/en/programs/cargo/dangerous-goods/dgr-guidance-passengers/"
    },
    {
      "title": "MMA2's passenger FAQ",
      "url": "https://mma2.ng/flight-and-airline/"
    }
  ],
  "abuja-airport-abv-terminals-services": [
    {
      "title": "FAAN's Passenger Experience Hub",
      "url": "https://faan.gov.ng/passenger-experience/"
    }
  ],
  "best-airport-lounges-nigeria-business-travel": [
    {
      "title": "Priority Pass Lagos directory",
      "url": "https://www.prioritypass.com/en-GB/lounges/nigeria/murtala-muhammed-international"
    },
    {
      "title": "official contact page",
      "url": "https://mma2.ng/help-contacts/"
    }
  ],
  "currency-exchange-nigeria-guide-2026": [
    {
      "title": "Central Bank of Nigeria's reforms information",
      "url": "https://www.cbn.gov.ng/AboutCBN/Reforms.html"
    },
    {
      "title": "CBN's IMTO directory",
      "url": "https://www.cbn.gov.ng/PaymentsSystem/InternationalMoneyTransferOperators.html"
    }
  ],
  "navigating-lagos-airport-los-guide": [
    {
      "title": "MMA2's passenger guidance",
      "url": "https://mma2.ng/flight-and-airline/"
    },
    {
      "title": "FAAN passenger support",
      "url": "https://faan.gov.ng/passenger-experience/"
    }
  ],
  "packing-essentials-nigerian-trip": [
    {
      "title": "Nigeria health guidance",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/health"
    }
  ],
  "lost-luggage-what-to-do-nigerian-airports": [
    {
      "title": "NCAA consumer-protection regulations",
      "url": "https://ncaa.gov.ng/media/co1nbi24/nig-cars-part-19-consumer-protection.pdf"
    },
    {
      "title": "ICAO's revised limits",
      "url": "https://www.icao.int/sites/default/files/secretariat/legal/LEB%20Treaty%20Collection%20Documents/2024_Revised_Limits_of_Liability_Under_the_Montreal_Convention_of_1999_en.pdf"
    },
    {
      "title": "UK government's air-passenger guide",
      "url": "https://www.gov.uk/government/publications/air-passenger-travel-guide/air-passenger-travel-guide"
    }
  ],
  "managing-layovers-international-airports": [
    {
      "title": "NIS visa categories",
      "url": "https://immigration.gov.ng/nigerian-visa/"
    },
    {
      "title": "official safety guidance",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/safety-and-security"
    }
  ],
  "understanding-nigerian-airport-codes": [
    {
      "title": "IATA airport-code search",
      "url": "https://www.iata.org/codes/"
    }
  ],
  "renting-car-nigerian-airports-guide": [
    {
      "title": "UK government's Nigeria safety guidance",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/safety-and-security"
    },
    {
      "title": "MMA2's car-park and transport page",
      "url": "https://mma2.ng/car-park/"
    }
  ],
  "sustainable-aviation-nigeria-eco-friendly": [
    {
      "title": "ICAO Carbon Emissions Calculator",
      "url": "https://icec.icao.int/Home/Index"
    },
    {
      "title": "methodology overview",
      "url": "https://www.icao.int/environmental-protection/environmental-tools/icec"
    },
    {
      "title": "calculator FAQ",
      "url": "https://icec.icao.int/FAQ"
    }
  ],
  "digital-nomad-travel-nigeria-guide": [
    {
      "title": "NIS visa directory",
      "url": "https://immigration.gov.ng/nigerian-visa/"
    },
    {
      "title": "NCC visitor-SIM guidance",
      "url": "https://www.ncc.gov.ng/frequently-asked-questions/foreigners-living-nigeria-or-visiting-abroad"
    }
  ],
  "domestic-vs-international-flights-nigeria": [
    {
      "title": "NIS's visa directory",
      "url": "https://immigration.gov.ng/nigerian-visa/"
    },
    {
      "title": "MMA2's flight information",
      "url": "https://mma2.ng/flight-and-airline/"
    }
  ],
  "essential-items-carry-on-nigeria-flights": [
    {
      "title": "IATA passenger guidance",
      "url": "https://www.iata.org/en/programs/cargo/dangerous-goods/dgr-guidance-passengers/"
    }
  ],
  "travel-insurance-guide-nigeria": [
    {
      "title": "FCDO travel-insurance guidance",
      "url": "https://www.gov.uk/guidance/foreign-travel-insurance"
    }
  ],
  "accessible-travel-nigeria-disability-guide": [
    {
      "title": "Ibom Air's baggage policy",
      "url": "https://www.ibomair.com/travel-information/baggage/"
    },
    {
      "title": "FAAN's Passenger Experience Hub",
      "url": "https://faan.gov.ng/passenger-experience/"
    },
    {
      "title": "NCAA consumer-protection regulations",
      "url": "https://ncaa.gov.ng/media/co1nbi24/nig-cars-part-19-consumer-protection.pdf"
    }
  ],
  "safety-in-nigeria-travel-guide": [
    {
      "title": "UK government's Nigeria guidance",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/safety-and-security"
    },
    {
      "title": "official health guidance for Nigeria",
      "url": "https://www.gov.uk/foreign-travel-advice/nigeria/health"
    }
  ]
};

export const blogPosts: BlogPost[] = rawBlogPosts.map(post => ({
  ...post,
  sources: articleSources[post.slug] ?? [],
  reviewedAt: articleSources[post.slug] ? '2026-10-23' : undefined,
  adsEligible: false
}));

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getAuthorById = (id: string): Author | undefined => {
  return authors.find(author => author.id === id);
};

export const getFeaturedPosts = (): BlogPost[] => {
  return blogPosts.filter(post => post.featured);
};

export const getPostsByCategory = (category: string): BlogPost[] => {
  return blogPosts.filter(post => post.category === category);
};

export const getPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find(post => post.slug === slug);
};

export const getRecentPosts = (limit: number = 5): BlogPost[] => {
  return [...blogPosts]
    .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
    .slice(0, limit);
};

export const searchPosts = (query: string): BlogPost[] => {
  const lowercaseQuery = query.toLowerCase();
  return blogPosts.filter(post =>
    post.title.toLowerCase().includes(lowercaseQuery) ||
    post.excerpt.toLowerCase().includes(lowercaseQuery) ||
    post.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
  );
};
